import { Tab } from '@/utils/tabs';
import { DownloadOutlined, SaveOutlined } from '@ant-design/icons';
import { useMutation, useQuery as useReactQuery } from '@tanstack/react-query';
import { App, Badge, Button, Form, Spin, Tabs, Tooltip } from 'antd';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthProvider';
import { setCompanies } from '../../../contextRedux/Company/CompanyStore';
import { setDevices } from '../../../contextRedux/DeviceType/DeviceTypeStore';
import { setLaborExtras } from '../../../contextRedux/LaborExtra/LaborExtraStore';
import { setPmContract, updateEquipmentTab, updateField } from '../../../contextRedux/PMContract/PmContractModel';
import { setPmContractStatuses } from '../../../contextRedux/PmContractStatus/PmContractStatusStore';
import { setPmOffers } from '../../../contextRedux/PMOffer/PmOfferStore';
import { setPmOfferContractTypes } from '../../../contextRedux/PmOfferContractTypes/PmOfferContractTypesStore';
import { setSystemTypes } from '../../../contextRedux/SystemType/SystemTypeStore';
import { setUnitMeasures } from '../../../contextRedux/UnitMeasure/UnitMeasureStore';
import { setUsers } from '../../../contextRedux/User/UserStore';
import PmContractStatus from '../../../enums/PmContractStatus';
import CompanyService from '../../../services/company/CompanyService';
import ConfigurationService from '../../../services/configuration/ConfigurationService';
import DeviceTypeService from '../../../services/deviceType/DeviceTypeService';
import LaborExtraService from '../../../services/laborExtra/LaborExtraService';
import PmContractService from '../../../services/pmContract/PmContractService';
import PmOfferService from '../../../services/pmOffer/PmOfferService';
import SystemTypeService from '../../../services/systemType/SystemTypeService';
import UserService from '../../../services/user/UserService';
import WorkPlaceService from '../../../services/workPlace/WorkPlaceService';
import { RootState } from '../../../store';
import { saveBlobCrossPlatform } from '../../../utils/saveBlobCrossPlatform';
import './NewPmContract.css';
import AddEquipmentTab from './Tabs/AddEquipmentTab/AddEquipmentTab';
import CommentsTab from './Tabs/CommentsTab/CommentsTab';
import CompanyDetailsTab from './Tabs/CompanyDetailsTab/CompanyDetailsTab';
import LaborTab from './Tabs/LaborTab/LaborTab';
import ReviewTab from './Tabs/ReviewTab/ReviewTab';
import SummaryTab from './Tabs/SummaryTab/SummaryTab';

const NewPmContract: React.FC = () => {
  const isMobile = useMediaQuery({ maxWidth: 720 });
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const useQuery = () => new URLSearchParams(useLocation().search);
  const query = useQuery();
  const [form] = Form.useForm();

  const [id, setId] = useState<string | null>(query.get('id'));

  const { message, modal } = App.useApp();
  const { profile } = useAuth();

  const reduxPmContract = useSelector((state: RootState) => state?.models.pmContract);
  const addedDevices = useSelector((state: RootState) => state.models.pmContract.pmContractWorkPlaceItems);
  const companies = useSelector((state: RootState) => state.store.company.data);

  let totalOfferValue =
    Number.parseFloat(
      (
        reduxPmContract.pmContractWorkPlaceItems.reduce((total, item) => total + item.laborPrice, 0) +
        (reduxPmContract.pmContractLaborExtras
          ? reduxPmContract.pmContractLaborExtras.reduce(
              (total: any, item: any) => total + item?.piece * item?.price,
              0
            )
          : 0)
      ).toFixed(2)
    ) +
    Number.parseFloat(
      reduxPmContract.pmContractWorkPlaceItems.reduce((total, item) => {
        const totalPrice = item.price;
        return total + totalPrice;
      }, 0)
    );

  const [canGeneratePVRLT, setCanGeneratePVRT] = useState<boolean>(false);
  const [canSavePmContract, setCanSavePmContract] = useState<boolean>(false);

  const userRols = 'sales';

  const isShowCompanyDetailsTab: boolean = !!(
    reduxPmContract.companyId &&
    reduxPmContract.accountManagerId &&
    reduxPmContract.accountSaleId &&
    reduxPmContract.enumPmOfferContractTypeId &&
    reduxPmContract.enumPmContractStatusId &&
    reduxPmContract.date &&
    reduxPmContract.dateStart &&
    reduxPmContract.dateEnd &&
    reduxPmContract.registrationNumber
  );

  const { data: _accountSales, isFetching: isUsersLoading } = useReactQuery({
    queryKey: ['users'],
    queryFn: () =>
      UserService.search({
        isActive: true,
        rols: userRols,
      }).then((data) => {
        dispatch(setUsers(data?.response));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  useEffect(() => {
    const idFromUrl = query.get('id');
    if (!idFromUrl) {
    }
    setId(idFromUrl);
  }, [location.search]);

  const { data: _pmContract, isFetching: isPmContractLoading } = useReactQuery({
    queryKey: ['pmContracts'],
    queryFn: () =>
      PmContractService.search({
        ids: [id!],
        isActive: true,
        isStatusIncluded: true,
        isTypeIncluded: true,
        isCompanyIncluded: true,
        isContractWorkPlaceIncluded: true,
        isAccountManagerIncluded: true,
        isAccountSaleIncluded: true,
      }).then((data) => {
        dispatch(setPmContract(data?.response[0]));
        return data?.response[0];
      }),
    enabled: !!id,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: _offer, isFetching: isPmOffersLoading } = useReactQuery({
    queryKey: ['pmOffers'],
    queryFn: () =>
      PmOfferService.search({
        companyId: reduxPmContract.companyId,
        isActive: true,
        isStatusIncluded: true,
        isCompanyIncluded: true,
        isPmOfferWorkPlaceIncluded: true,
        isAccountManagerIncluded: true,
        isAccountSaleIncluded: true,
      }).then((data) => {
        dispatch(setPmOffers(data?.response));
        return data?.response;
      }),
    enabled: !!reduxPmContract.companyId,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: _status, isFetching: isStatusLoading } = useReactQuery({
    queryKey: ['pmContractStatuses'],
    queryFn: () =>
      ConfigurationService.searchPmContractStatus({}).then((data) => {
        dispatch(setPmContractStatuses(data?.response));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: _contractTypes, isFetching: isPmOfferTypesLoading } = useReactQuery({
    queryKey: ['pmOfferContractTypes'],
    queryFn: () =>
      ConfigurationService.searchPmOfferContractType({}).then((data) => {
        dispatch(setPmOfferContractTypes(data?.response?.filter((pc: any) => [1, 3].includes(pc.id))));
        return data?.response?.filter((pc: any) => [1, 3].includes(pc.id));
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: _companies, isFetching: isCompaniesLoading } = useReactQuery({
    queryKey: ['companies'],
    queryFn: () =>
      CompanyService.search({
        isAgentIncluded: true,
        isWorkPlacesIncluded: true,
        isAccountManagerIncluded: true,
        isActive: true,
      }).then((data) => {
        dispatch(setCompanies(data?.response));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: _unitMeasures, isFetching: isUnitMeasuresLoading } = useReactQuery({
    queryKey: ['unitMeasures'],
    queryFn: () =>
      ConfigurationService.searchConfiguration({ tag: 'unitMeasure' }).then((data) => {
        dispatch(setUnitMeasures(data?.response?.section));

        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: _laborExtras, isFetching: isLaborExtrasLoading } = useReactQuery({
    queryKey: ['laborExtras'],
    queryFn: () =>
      LaborExtraService.search({ isActive: true }).then((data) => {
        dispatch(setLaborExtras(data?.response));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: _systemTypes, isFetching: isSystemTypesLoading } = useReactQuery({
    queryKey: ['systemTypes'],
    queryFn: () =>
      SystemTypeService.search({
        isDeviceTypeIncluded: true,
        isActive: true,
      }).then((data) => {
        dispatch(setSystemTypes(data?.response));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: _deviceTypes, isFetching: isDeviceTypesLoading } = useReactQuery({
    queryKey: ['deviceTypes'],
    queryFn: () =>
      DeviceTypeService.search({
        isSystemTypeIncluded: true,
        isLaborIncluded: true,
        isActive: true,
      }).then((data) => {
        dispatch(setDevices(data?.response));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const {
    data: _pmContractComments,
    refetch: searchComments,
    isFetching: isPmContractCommentsLoading,
  } = useReactQuery({
    queryKey: ['pmContractComment'],
    queryFn: () =>
      PmContractService.searchComment({
        ids: [id!],
      }).then((data) => {
        const departments = ['general', 'contabilitate', 'manager', 'instalatori'];

        // Initialize offerComments with all departments
        const initialContractComments = departments.map((departmentName) => {
          const existingComment = data?.response?.find((comment: any) => comment.departmentName === departmentName);
          return (
            existingComment || {
              id: '',
              pmContractId: id,
              departmentName,
              message: '',
              pmContractCommentFiles: [],
            }
          );
        });
        dispatch(updateField({ field: 'pmContractCommentsTab', value: initialContractComments }));
        return data?.response;
      }),
    enabled: !!id,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { mutate: saveContract, isPending: isSavingContract } = useMutation({
    mutationFn: (formData: any) => PmContractService.upsert(id, formData),
    onSuccess: (data: any) => {
      if (data) {
        handleResponse(data);
        data?.response && navigate(`/project-management/pmContract/edit?id=${data?.response?.id}`);
        if (!!id) {
          searchComments();
          if (data?.response?.enumPmContractStatusId == 3) {
            setCanGeneratePVRT(true);
          } else setCanGeneratePVRT(false);
        }
      } else {
        console.error('Received data is not in JSON format or JSON parsing failed.');
      }
    },
  });

  const onSaveContract = () => {
    saveContract({
      ...reduxPmContract,
      pmContractComments: reduxPmContract.pmContractCommentsTab,
      pmContractIntervals: form?.getFieldValue('pmContractIntervals'),
    });

    setCanSavePmContract(
      addedDevices?.some((item: any) => item.addition < 50)! &&
        reduxPmContract.enumPmContractStatus.name !== PmContractStatus.Aprobat
    );
  };

  const { mutate: updateWorkPace } = useMutation({
    mutationFn: (formData: any) => WorkPlaceService.upsert(formData.id, formData),
    onSuccess: () => {},
    onError: () => {
      message.error('Eroare procesare request.');
    },
  });

  const filterAndGroupData = (data: any) => {
    const result: any = {};

    data.forEach((item: any) => {
      const { workPlace, workPlaceId, systemTypeId, deviceTypeId, piece, isAnotherProvider } = item;

      if (!result[workPlaceId]) {
        result[workPlaceId] = {
          workPlace: workPlace,
          workPlaceId: workPlaceId,
          devices: [],
        };
      }

      result[workPlaceId].devices.push({
        systemTypeId,
        deviceTypeId,
        piece,
        isAnotherProvider,
      });
    });

    return Object.values(result);
  };

  const handleSaveContract = async () => {
    try {
      modal.confirm({
        title: 'Confirm Update',
        content: 'Sigur doriți să actualizați punctele de lucru cu configurația curentă?',
        okText: 'Da',
        cancelText: 'Nu',
        onOk: async () => {
          const workPlaceItems = filterAndGroupData(addedDevices);
          workPlaceItems.forEach((workPlaceItem: any) => {
            const formData = {
              ...workPlaceItem?.workPlace,
              agents:
                companies
                  .find((c: any) => c.id == workPlaceItem?.workPlace?.companyId)
                  .workPlaces.find((wp: any) => wp.id == workPlaceItem.workPlace.id)?.agents ?? [],
              contactPersons:
                companies
                  .find((c: any) => c.id == workPlaceItem?.workPlace?.companyId)
                  .workPlaces.find((wp: any) => wp.id == workPlaceItem?.workPlace?.id)?.contactPersons ?? [],
              workPlaceItems: workPlaceItem.devices.map((d: any) => {
                return {
                  id: '',
                  workPlaceId: workPlaceItem.workPlaceId,
                  deviceTypeId: d.deviceTypeId,
                  systemTypeId: d.systemTypeId,
                  piece: d.piece,
                  metadata: '{}',
                  isAnotherProvider: d.isAnotherProvider ?? false,
                };
              }),
            };
            updateWorkPace(formData);
          });
          await onSaveContract();
        },
        onCancel: async () => {
          await onSaveContract();
        },
      });
    } catch (errorInfo: any) {
      message.error(errorInfo?.errorFields[0]?.errors);
    }
  };

  const handleResponse = (data: any) => {
    if (data?.errors?.length > 0) {
      data?.errors?.forEach((item: any) => {
        message.error(item);
      });
    } else {
      message.success(
        <span>
          <span className='font-weight-bold'>{data?.response?.number}</span> a fost {!!id ? 'modificat' : 'adaugat'} cu
          success
        </span>
      );
      navigate(`/project-management/pmContract/edit?id=${data?.response?.id}`);
    }
  };

  const { mutate: viewPdfPmContract, isPending: isViewingPdfPmContract } = useMutation({
    mutationFn: (formData: any) => PmContractService.preview(id, formData),
    onSuccess: (data: any) => {
      if (!data || !data.blob) {
        throw new Error('Invalid response received from the server');
      }
      return data.blob().then((blob: any) => {
        const url = window.URL.createObjectURL(blob);
        dispatch(
          updateEquipmentTab({
            field: 'pdfUrl',
            value: url,
          })
        );
      });
    },
    onError: () => {
      message.error('Eroare procesare request.');
    },
  });

  const viewPdf = () => {
    if (isShowCompanyDetailsTab) {
      viewPdfPmContract(reduxPmContract);
    }
  };

  const { mutate: printPmContractToPdf, isPending: isPrintingPmContract } = useMutation({
    mutationFn: () =>
      PmContractService.print({
        ids: [id!],
        isStatusIncluded: true,
        isCompanyIncluded: true,
        isContractWorkPlaceIncluded: true,
        isAccountManagerIncluded: true,
        isAccountSaleIncluded: true,
      }),
    onSuccess: (response: any) => {
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'file.pdf';

      if (contentDisposition) {
        const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        const matches = filenameRegex.exec(contentDisposition);
        if (matches != null && matches[1]) {
          filename = matches[1].replace(/['"]/g, '');
        }
      }

      return response.blob().then((blob: any) => {
        saveBlobCrossPlatform(blob, filename);
      });
    },
    onError: () => {
      message.error('Eroare procesare request.');
    },
  });

  const { mutate: generatePVRL, isPending: isGeneratingPVRLT } = useMutation({
    mutationFn: () =>
      PmContractService.generatePVRLT({
        ids: [id!],
        isStatusIncluded: true,
        isCompanyIncluded: true,
        isContractWorkPlaceIncluded: true,
      }),
    onSuccess: (response: any) => {
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'file.pdf';

      if (contentDisposition) {
        const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        const matches = filenameRegex.exec(contentDisposition);
        if (matches != null && matches[1]) {
          filename = matches[1].replace(/['"]/g, '');
        }
      }

      return response.blob().then((blob: any) => {
        saveBlobCrossPlatform(blob, filename);
      });
    },
    onError: () => {
      message.error('Eroare procesare request.');
    },
  });

  const tabs: (Tab | false)[] = [
    {
      key: 'companyTab',
      label: 'Detalii Client',
      children: <CompanyDetailsTab form={form} />,
    },
    isShowCompanyDetailsTab && {
      key: 'deviceTab',
      label: 'Adaugare Echipament',
      children: <AddEquipmentTab />,
    },
    addedDevices?.length > 0 && {
      key: 'summaryTab',
      label: (
        <>
          Vizualizare Echipamente Adaugate
          <Badge
            color='blue'
            style={{ marginLeft: '5px' }}
            count={addedDevices.length > 0 ? addedDevices.length : ''}
          ></Badge>
        </>
      ),
      children: <SummaryTab />,
    },
    addedDevices?.length > 0 && {
      key: 'manoperaTab',
      label: 'Manopera',
      children: <LaborTab />,
    },
    addedDevices?.length! > 0 && {
      key: 'commentsTab',
      label: 'Observatii si Comentarii',
      children: <CommentsTab />,
    },
    addedDevices?.length! > 0 &&
      isShowCompanyDetailsTab && {
        key: 'reviewTab',
        label: 'Review',
        children: <ReviewTab isViewingPdfPmContract={isViewingPdfPmContract} />, // Use key to force re-render
      },
  ];

  const filteredTabs: Tab[] = tabs.filter((tab): tab is Tab => tab !== false);

  if (
    isPmContractLoading ||
    isCompaniesLoading ||
    isLaborExtrasLoading ||
    isStatusLoading ||
    isPmOfferTypesLoading ||
    isUnitMeasuresLoading ||
    isSystemTypesLoading ||
    isDeviceTypesLoading ||
    isPrintingPmContract ||
    isSavingContract ||
    isPmContractCommentsLoading ||
    isGeneratingPVRLT ||
    isUsersLoading ||
    isPmOffersLoading
  ) {
    return <Spin size='large' className='spinner' />;
  }

  return (
    <>
      <div className='topLine-pm'>
        <h2>{id ? 'Contract de instalare - ' + reduxPmContract.number : 'Contract nou de instalare'}</h2>
        <div className='totalLine-pm'>
          <h3>{'Total - ' + totalOfferValue + ' lei'}</h3>
          {addedDevices?.some((item: any) => item.addition < 50)! && (
            <h2 style={{ color: 'red' }}>Adaosul produselor s-a modificat sub 50%</h2>
          )}
          <div className='buttons-pm'>
            <Button
              onClick={() => handleSaveContract()}
              className='saveBtn-pm'
              disabled={
                profile?.userPermission?.rols?.some((role: any) => role == 'senior_sales') ? false : canSavePmContract
              }
            >
              {isMobile ? (
                <Tooltip title='Salveaza Contract'>
                  <SaveOutlined />
                </Tooltip>
              ) : (
                'Salveaza Contract'
              )}
            </Button>

            {id!! && addedDevices?.length > 0 && (
              <Button
                className='download-pdf-pm'
                onClick={() => printPmContractToPdf()}
                disabled={
                  profile?.userPermission?.rols?.some((role: any) => role == 'senior_sales') ? false : canSavePmContract
                }
              >
                {isMobile ? (
                  <Tooltip title='Descărcare PDF'>
                    <DownloadOutlined />
                  </Tooltip>
                ) : (
                  <>
                    {' '}
                    <DownloadOutlined /> Descărcare PDF{' '}
                  </>
                )}
              </Button>
            )}

            {canGeneratePVRLT && (
              <Button className='download-pvrlt-pm' onClick={() => generatePVRL()}>
                {isMobile ? (
                  <Tooltip title='Genereaza PVRLT'>
                    <DownloadOutlined /> PVRLT
                  </Tooltip>
                ) : (
                  <>
                    {' '}
                    <DownloadOutlined /> Genereaza PVRLT{' '}
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
      <Tabs
        className='tabs'
        defaultActiveKey='1'
        items={filteredTabs}
        tabBarStyle={{ backgroundColor: '#DADBDD' }}
        tabBarGutter={18}
        onChange={(key) => {
          if (key === 'reviewTab') {
            viewPdf(); // Increment the key to force re-render
          }
        }}
      />
    </>
  );
};

export default NewPmContract;
